import type { OverrideTokens } from '@ovComponents/0-tokens/overrideTypes';
import type { Hexcode } from '@ovComponents/0-tokens/ref/palette/palette';
import type { RefTokens } from '@ovComponents/0-tokens/ref/refTokens';
import { useRefTokens } from '@ovComponents/0-tokens/ref/refTokens';
import { merge } from 'lodash-es';

export type ColorTokens = {
  primary?: Hexcode;
  primaryHover?: Hexcode;
  primaryPressed?: Hexcode;
  onPrimary?: Hexcode;
  primaryContainer?: Hexcode;
  primaryVariant?: Hexcode;
  primaryVariantHover?: Hexcode;
  primaryVariantPressed?: Hexcode;
  onPrimaryVariant?: Hexcode;
  negative?: Hexcode;
  negativeHover?: Hexcode;
  negativePressed?: Hexcode;
  onNegative?: Hexcode;
  negativeVariant?: Hexcode;
  negativeVariantHover?: Hexcode;
  negativeVariantPressed?: Hexcode;
  onNegativeVariant?: Hexcode;
  negativeOutline?: Hexcode;
  positive?: Hexcode;
  positiveHover?: Hexcode;
  positivePressed?: Hexcode;
  onPositive?: Hexcode;
  positiveVariant?: Hexcode;
  positiveVariantHover?: Hexcode;
  positiveVariantPressed?: Hexcode;
  onPositiveVariant?: Hexcode;
  warning?: Hexcode;
  warningHover?: Hexcode;
  warningPressed?: Hexcode;
  onWarning?: Hexcode;
  warningVariant?: Hexcode;
  warningVariantHover?: Hexcode;
  warningVariantPressed?: Hexcode;
  onWarningVariant?: Hexcode;
  neutralHover?: Hexcode;
  neutralPressed?: Hexcode;
  outline?: Hexcode;
  outlineVariant?: Hexcode;
  surface?: Hexcode;
  onSurface?: Hexcode;
  onSurfaceVariant?: Hexcode;
  surfaceContainer?: Hexcode;
  surfaceContainerVariant?: Hexcode;
  background?: Hexcode;
  onBackground?: Hexcode;
  disabled?: Hexcode;
  onDisabled?: Hexcode;
  navigation?: Hexcode;
  onNavigation?: Hexcode;
  supportOneVariant?: Hexcode;
  supportOneOnVariant?: Hexcode;
  supportOneNew?: Hexcode;
  supportOneNewVariant?: Hexcode;
  supportOneOnNewVariant?: Hexcode;
  supportTwo?: Hexcode;
  supportTwoVariant?: Hexcode;
  supportTwoOnVariant?: Hexcode;
  supportThree?: Hexcode;
  supportThreeVariant?: Hexcode;
  supportThreeOnVariant?: Hexcode;
  supportFour?: Hexcode;
  supportFourVariant?: Hexcode;
  supportFourOnVariant?: Hexcode;
  supportFive?: Hexcode;
  supportFiveVariant?: Hexcode;
  supportFiveOnVariant?: Hexcode;
  supportSix?: Hexcode;
  supportSixVariant?: Hexcode;
  supportSixOnVariant?: Hexcode;
  supportPositiveOne?: Hexcode;
  supportPositiveOneVariant?: Hexcode;
  supportPositiveOneOnVariant?: Hexcode;
  supportPositiveTwo?: Hexcode;
  supportPositiveTwoVariant?: Hexcode;
  supportPositiveTwoOnVariant?: Hexcode;
  supportPositiveThree?: Hexcode;
  supportPositiveThreeVariant?: Hexcode;
  supportPositiveThreeOnVariant?: Hexcode;
  supportPositiveFour?: Hexcode;
  supportPositiveFourVariant?: Hexcode;
  supportPositiveFourOnVariant?: Hexcode;
  supportPositiveFive?: Hexcode;
  supportPositiveFiveVariant?: Hexcode;
  supportPositiveFiveOnVariant?: Hexcode;
  supportPositiveSix?: Hexcode;
  supportPositiveSixVariant?: Hexcode;
  supportPositiveSixOnVariant?: Hexcode;
  supportNegativeOne?: Hexcode;
  supportNegativeOneVariant?: Hexcode;
  supportNegativeOneOnVariant?: Hexcode;
  supportNegativeTwo?: Hexcode;
  supportNegativeTwoVariant?: Hexcode;
  supportNegativeTwoOnVariant?: Hexcode;
  supportNegativeThree?: Hexcode;
  supportNegativeThreeVariant?: Hexcode;
  supportNegativeThreeOnVariant?: Hexcode;
  supportNegativeFour?: Hexcode;
  supportNegativeFourVariant?: Hexcode;
  supportNegativeFourOnVariant?: Hexcode;
  supportNegativeFive?: Hexcode;
  supportNegativeFiveVariant?: Hexcode;
  supportNegativeFiveOnVariant?: Hexcode;
  supportNegativeSix?: Hexcode;
  supportNegativeSixVariant?: Hexcode;
  supportNegativeSixOnVariant?: Hexcode;
};

export const useColorTokens = (override: OverrideTokens): ColorTokens => {
  const ref: RefTokens = useRefTokens(override);
  const refTokens: RefTokens = merge({}, ref, override.ref);

  return {
    // Primary
    primary: refTokens.palette?.primary50,
    primaryHover: refTokens.palette?.primary60,
    primaryPressed: refTokens.palette?.primary70,
    onPrimary: refTokens.palette?.neutral10,
    primaryContainer: refTokens.palette?.neutral30,

    primaryVariant: refTokens.palette?.primary20,
    primaryVariantHover: refTokens.palette?.primary30,
    primaryVariantPressed: refTokens.palette?.primary40,
    onPrimaryVariant: refTokens.palette?.primary70,

    // Negative
    negative: refTokens.palette?.negative50,
    negativeHover: refTokens.palette?.negative60,
    negativePressed: refTokens.palette?.negative70,
    onNegative: refTokens.palette?.neutral10,

    negativeVariant: refTokens.palette?.negative20,
    negativeVariantHover: refTokens.palette?.negative30,
    negativeVariantPressed: refTokens.palette?.negative40,
    onNegativeVariant: refTokens.palette?.negative70,
    negativeOutline: refTokens.palette?.negative60,

    // Positive
    positive: refTokens.palette?.positive50,
    positiveHover: refTokens.palette?.positive60,
    positivePressed: refTokens.palette?.positive70,
    onPositive: refTokens.palette?.neutral10,

    positiveVariant: refTokens.palette?.positive20,
    positiveVariantHover: refTokens.palette?.positive30,
    positiveVariantPressed: refTokens.palette?.positive40,
    onPositiveVariant: refTokens.palette?.positive70,

    // Warning
    warning: refTokens.palette?.warning50,
    warningHover: refTokens.palette?.warning60,
    warningPressed: refTokens.palette?.warning70,
    onWarning: refTokens.palette?.neutral10,

    warningVariant: refTokens.palette?.warning20,
    warningVariantHover: refTokens.palette?.warning30,
    warningVariantPressed: refTokens.palette?.warning40,
    onWarningVariant: refTokens.palette?.warning70,

    // Support
    supportOneVariant: refTokens.palette?.primary20,
    supportOneOnVariant: refTokens.palette?.primary60,

    supportOneNew: refTokens.palette?.supportOne50,
    supportOneNewVariant: refTokens.palette?.supportOne40,
    supportOneOnNewVariant: refTokens.palette?.supportOne60,

    supportTwo: refTokens.palette?.supportTwo50,
    supportTwoVariant: refTokens.palette?.supportTwo40,
    supportTwoOnVariant: refTokens.palette?.supportTwo60,

    supportThree: refTokens.palette?.supportThree50,
    supportThreeVariant: refTokens.palette?.supportThree40,
    supportThreeOnVariant: refTokens.palette?.supportThree60,

    supportFour: refTokens.palette?.supportFour50,
    supportFourVariant: refTokens.palette?.supportFour40,
    supportFourOnVariant: refTokens.palette?.supportFour60,

    supportFive: refTokens.palette?.supportFive50,
    supportFiveVariant: refTokens.palette?.supportFive40,
    supportFiveOnVariant: refTokens.palette?.supportFive60,

    supportSix: refTokens.palette?.supportSix50,
    supportSixVariant: refTokens.palette?.supportSix40,
    supportSixOnVariant: refTokens.palette?.supportSix60,

    // Support positive
    supportPositiveOne: refTokens.palette?.supportPositiveOne50,
    supportPositiveOneVariant: refTokens.palette?.supportPositiveOne40,
    supportPositiveOneOnVariant: refTokens.palette?.supportPositiveOne60,

    supportPositiveTwo: refTokens.palette?.supportPositiveTwo50,
    supportPositiveTwoVariant: refTokens.palette?.supportPositiveTwo40,
    supportPositiveTwoOnVariant: refTokens.palette?.supportPositiveTwo60,

    supportPositiveThree: refTokens.palette?.supportPositiveThree50,
    supportPositiveThreeVariant: refTokens.palette?.supportPositiveThree40,
    supportPositiveThreeOnVariant: refTokens.palette?.supportPositiveThree60,

    supportPositiveFour: refTokens.palette?.supportPositiveFour50,
    supportPositiveFourVariant: refTokens.palette?.supportPositiveFour40,
    supportPositiveFourOnVariant: refTokens.palette?.supportPositiveFour60,

    supportPositiveFive: refTokens.palette?.supportPositiveFive50,
    supportPositiveFiveVariant: refTokens.palette?.supportPositiveFive40,
    supportPositiveFiveOnVariant: refTokens.palette?.supportPositiveFive60,

    supportPositiveSix: refTokens.palette?.supportPositiveSix50,
    supportPositiveSixVariant: refTokens.palette?.supportPositiveSix40,
    supportPositiveSixOnVariant: refTokens.palette?.supportPositiveSix60,

    // Support negative
    supportNegativeOne: refTokens.palette?.supportNegativeOne50,
    supportNegativeOneVariant: refTokens.palette?.supportNegativeOne40,
    supportNegativeOneOnVariant: refTokens.palette?.supportNegativeOne60,

    supportNegativeTwo: refTokens.palette?.supportNegativeTwo50,
    supportNegativeTwoVariant: refTokens.palette?.supportNegativeTwo40,
    supportNegativeTwoOnVariant: refTokens.palette?.supportNegativeTwo60,

    supportNegativeThree: refTokens.palette?.supportNegativeThree50,
    supportNegativeThreeVariant: refTokens.palette?.supportNegativeThree40,
    supportNegativeThreeOnVariant: refTokens.palette?.supportNegativeThree60,

    supportNegativeFour: refTokens.palette?.supportNegativeFour50,
    supportNegativeFourVariant: refTokens.palette?.supportNegativeFour40,
    supportNegativeFourOnVariant: refTokens.palette?.supportNegativeFour60,

    supportNegativeFive: refTokens.palette?.supportNegativeFive50,
    supportNegativeFiveVariant: refTokens.palette?.supportNegativeFive40,
    supportNegativeFiveOnVariant: refTokens.palette?.supportNegativeFive60,

    supportNegativeSix: refTokens.palette?.supportNegativeSix50,
    supportNegativeSixVariant: refTokens.palette?.supportNegativeSix40,
    supportNegativeSixOnVariant: refTokens.palette?.supportNegativeSix60,

    // Neutral
    neutralHover: refTokens.palette?.neutral20,
    neutralPressed: refTokens.palette?.neutral30,

    // Outline
    outline: refTokens.palette?.neutral60,
    outlineVariant: refTokens.palette?.neutral40,

    // Surface
    surface: refTokens.palette?.neutral10,
    onSurface: refTokens.palette?.neutral90,
    onSurfaceVariant: refTokens.palette?.neutral70,
    surfaceContainer: refTokens.palette?.neutral20,
    surfaceContainerVariant: refTokens.palette?.neutral30,

    // Background
    background: refTokens.palette?.neutral20,
    onBackground: refTokens.palette?.neutral90,

    // Disabled
    disabled: refTokens.palette?.neutral30,
    onDisabled: refTokens.palette?.neutral60,

    // Navigation
    navigation: refTokens.palette?.primary50,
    onNavigation: refTokens.palette?.neutral10,
  };
};
